
import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { PerformanceOptimizer } from './components/PerformanceOptimizer';
import { SEORobotsTag } from './components/SEORobotsTag';
import { ProductList } from './components/ProductList';
import { ProductDetail } from './components/ProductDetail';
import { PriceDropsPage } from './components/PriceDropsPage';
import { DailyDealsPage } from './components/DailyDealsPage';
import { NewReleasesPage } from './components/NewReleasesPage';
import { TrendingPage } from './components/TrendingPage';
import { FilterPage } from './components/FilterPage';
import { WishlistPage } from './components/WishlistPage';
import { ComparePage } from './components/ComparePage';
import { DynamicPage } from './components/DynamicPage';
import { PageTypeList } from './components/PageTypeList';
import { AllPagesList } from './components/AllPagesList';
import { ProductCarousel } from './components/ProductCarousel';
import { Sidebar } from './components/ui/Sidebar';
import { MobileFilters } from './components/ui/MobileFilters';
import { ViewToggle } from './components/ui/ViewToggle';
import { DealsSection } from './components/DealsSection';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NotFoundPage } from './components/NotFoundPage';
import { ModernHeader } from './components/ui/ModernHeader';
import { ModernHomePage } from './components/ModernHomePage';
import { CategoryPage } from './components/CategoryPage';
import { SearchPage } from './components/SearchPage';
import { TagPage } from './components/TagPage';
import { LightRays1, LightRays2 } from './components/bg/LightRays';

import useAnalytics from './hooks/useAnalytics';

import { Helmet } from 'react-helmet-async';

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

function Analytics() {
  if (!GA_ID) return null;

  return (
    <Helmet>
      <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`} />
      <script>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_ID}', { anonymize_ip: true });
        `}
      </script>
    </Helmet>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30000,
      refetchOnWindowFocus: false
    },
  },
});

function HomePage() {
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    productType: 'all',
    minPrice: '',
    maxPrice: '',
    stockStatus: '',
    sort: 'featured'
  });

  const [view, setView] = useState('list');
  const [homePageKey, setHomePageKey] = useState(0);

  const handleNavigateHome = () => {
    setFilters({
      search: '',
      category: 'all',
      productType: 'all',
      minPrice: '',
      maxPrice: '',
      stockStatus: '',
      sort: 'featured'
    });
    // Force re-render of homepage by changing key
    setHomePageKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen dark:bg-slate-900 transition-colors">
      <main className="container mx-auto px-0 md:px-4 sm:px-6 lg:px-8 py-8">
        <ErrorBoundary>
          <ModernHomePage key={homePageKey} onNavigateHome={handleNavigateHome} />
        </ErrorBoundary>
      </main>

      <MobileFilters filters={filters} onFilterChange={setFilters} />
    </div>
  );
}

function CategoryPageWrapper() {
  const { categorySlug, filter } = useParams();
  return <CategoryPage slug={categorySlug} filter={filter} />;
}

function SearchPageWrapper() {
  const { keyword } = useParams();
  return <SearchPage keyword={keyword || ''} />;
}

function HomePageWrapper() {
  return <HomePage />;
}

// Placeholder components for routes that aren't implemented yet
function PlaceholderPage({ title, description }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">{title}</h1>
        <p className="text-gray-600 mb-8">{description}</p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <p className="text-blue-800">This page is coming soon! We're working on bringing you the best {title.toLowerCase()}.</p>
        </div>
      </div>
    </div>
  );
}

function AppRoutes() {
  // MUST be inside Router
  useAnalytics();

  return (
    <>
      {/* ✅ Load GA as early as possible */}
      <Analytics />

      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <WishlistProvider>
            <PerformanceOptimizer />
            <SEORobotsTag />
              
              <div className="min-h-screen bg-slate-50 dark:bg-[#080d18] transition-colors">
                
                <LightRays2 />

                <ErrorBoundary>
                  <Routes>
                    {/* Product Detail Route */}
                    <Route 
                      path="/dp/:asin" 
                      element={<ProductDetail />} 
                    />

                    {/* Special Pages */}
                    <Route 
                      path="/daily-deals" 
                      element={
                        <>
                          <ModernHeader />
                          <DailyDealsPage />
                        </>
                      } 
                    />
                    <Route 
                      path="/price-drops-today" 
                      element={
                        <>
                          <ModernHeader />
                          <PriceDropsPage />
                        </>
                      } 
                    />
                    <Route 
                      path="/trending" 
                      element={
                        <>
                          <ModernHeader />
                          <TrendingPage />
                        </>
                      } 
                    />
                    <Route 
                      path="/new-releases" 
                      element={
                        <>
                          <ModernHeader />
                          <NewReleasesPage />
                        </>
                      } 
                    />

                    {/* Comparision vs Pages */}
                    <Route 
                      path="/compare" 
                      element={
                        <>
                          <ModernHeader />
                          <ComparePage />
                        </>
                      } 
                    />

                    {/* Wishlist Page */}
                    <Route 
                      path="/wishlist" 
                      element={
                        <>
                          <ModernHeader />
                          <WishlistPage />
                        </>
                      } 
                    />

                    {/* All Pages */}
                    <Route
                      path="/pages"
                      element={
                        <>
                          <ModernHeader />
                          <AllPagesList />
                        </>
                      }
                    />

                    {/* Single Slug Routes (must come before /:pageType/:slug) */}
                    <Route 
                      path="/:slug" 
                      element={
                        <>
                          <ModernHeader />
                          <DynamicPage />
                        </>
                      }
                    />

                    {/* Dynamic Pages Detail - More specific routes */}
                    <Route 
                      path="/:pageType/:slug" 
                      element={
                        <>
                          <ModernHeader />
                          <DynamicPage />
                        </>
                      } 
                    />

                    {/* Dynamic Pages List - Only for known page types */}
                    <Route
                      path="/brand"
                      element={
                        <>
                          <ModernHeader />
                          <PageTypeList />
                        </>
                      }
                    />
                    <Route
                      path="/feature"
                      element={
                        <>
                          <ModernHeader />
                          <PageTypeList />
                        </>
                      }
                    />
                    <Route
                      path="/deal"
                      element={
                        <>
                          <ModernHeader />
                          <PageTypeList />
                        </>
                      }
                    />
                    <Route
                      path="/use-case"
                      element={
                        <>
                          <ModernHeader />
                          <PageTypeList />
                        </>
                      }
                    />
                    <Route
                      path="/budget"
                      element={
                        <>
                          <ModernHeader />
                          <PageTypeList />
                        </>
                      }
                    />

                    {/* Category Routes - MUST BE SPECIFIC */}
                    <Route 
                      path="/category/:categorySlug" 
                      element={
                        <>
                          <ModernHeader />
                          <CategoryPageWrapper />
                        </>
                      } 
                    />
                    <Route 
                      path="/category/:categorySlug/:filter" 
                      element={
                        <>
                          <ModernHeader />
                          <CategoryPageWrapper />
                        </>
                      } 
                    />

                    {/* Search Routes */}
                    <Route 
                      path="/search/:keyword"
                      element={
                        <>
                          <ModernHeader />
                          <SearchPageWrapper />
                        </>
                      }
                    />

                    {/* Tag Routes */}
                    <Route 
                      path="/tag/:tagSlug"
                      element={
                        <>
                          <ModernHeader />
                          <TagPage />
                        </>
                      }
                    />

                    {/* Filter Routes - SEO URL Architecture (Most Specific First) */}
                    <Route 
                      path="/tag/:tagSlug/category/:categorySlug/:subSlug/filter"
                      element={
                        <>
                          <ModernHeader />
                          <FilterPage />
                        </>
                      }
                    />
                    <Route 
                      path="/tag/:tagSlug/category/:categorySlug/filter"
                      element={
                        <>
                          <ModernHeader />
                          <FilterPage />
                        </>
                      }
                    />
                    <Route 
                      path="/category/:categorySlug/:subSlug/filter"
                      element={
                        <>
                          <ModernHeader />
                          <FilterPage />
                        </>
                      }
                    />
                    <Route 
                      path="/category/:categorySlug/filter"
                      element={
                        <>
                          <ModernHeader />
                          <FilterPage />
                        </>
                      }
                    />
                    <Route 
                      path="/tag/:tagSlug/filter"
                      element={
                        <>
                          <ModernHeader />
                          <FilterPage />
                        </>
                      }
                    />
                    <Route 
                      path="/filter"
                      element={
                        <>
                          <ModernHeader />
                          <FilterPage />
                        </>
                      }
                    />

                    {/* Placeholder Routes */}
                    <Route 
                      path="/feature-pages" 
                      element={
                        <>
                          <ModernHeader />
                          <PlaceholderPage 
                            title="Feature Pages" 
                            description="Discover products by their key features and capabilities."
                          />
                        </>
                      } 
                    />
                    <Route 
                      path="/use-case-pages" 
                      element={
                        <>
                          <ModernHeader />
                          <PlaceholderPage 
                            title="Use Case Pages" 
                            description="Find the perfect products for your specific needs and use cases."
                          />
                        </>
                      } 
                    />
                    <Route 
                      path="/news" 
                      element={
                        <>
                          <ModernHeader />
                          <PlaceholderPage 
                            title="Tech News" 
                            description="Stay updated with the latest technology news and announcements."
                          />
                        </>
                      } 
                    />
                    <Route 
                      path="/buying-guides" 
                      element={
                        <>
                          <ModernHeader />
                          <PlaceholderPage 
                            title="Buying Guides" 
                            description="Expert guides to help you make informed purchasing decisions."
                          />
                        </>
                      } 
                    />
                    <Route 
                      path="/reviews" 
                      element={
                        <>
                          <ModernHeader />
                          <PlaceholderPage 
                            title="Product Reviews" 
                            description="In-depth reviews and analysis of the latest tech products."
                          />
                        </>
                      } 
                    />
                    <Route 
                      path="/faqs" 
                      element={
                        <>
                          <ModernHeader />
                          <PlaceholderPage 
                            title="Frequently Asked Questions" 
                            description="Find answers to common questions about products and shopping."
                          />
                        </>
                      } 
                    />
                    <Route 
                      path="/tools/price-tracker" 
                      element={
                        <>
                          <ModernHeader />
                          <PlaceholderPage 
                            title="Price Tracker & Alerts" 
                            description="Set up price alerts and track your favorite products."
                          />
                        </>
                      } 
                    />
                    <Route 
                      path="/tools/price-history" 
                      element={
                        <>
                          <ModernHeader />
                          <PlaceholderPage 
                            title="Price History Charts" 
                            description="View detailed price history and trends for any product."
                          />
                        </>
                      } 
                    />


                    {/* Home Route */}
                    <Route 
                      path="/" 
                      element={
                        <>
                          <ModernHeader onNavigateHome={() => window.location.reload()} />
                          <HomePageWrapper />
                        </>
                      } 
                    />

                    {/* 404 Route - MUST BE LAST */}
                    <Route 
                      path="*" 
                      element={
                        <>
                          <ModernHeader />
                          <NotFoundPage />
                        </>
                      } 
                    />
                  </Routes>
                </ErrorBoundary>

                <footer className="bg-slate-100 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                  <div className="container mx-auto py-8 px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    <p>&copy; 2025 PricePulse. All Rights Reserved.</p>
                    <p className="mt-1">Your ultimate guide to smart tech shopping.</p>
                  </div>
                </footer>
              </div>
              
            </WishlistProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
}

function App() {
  return (
    <Router>
      <HelmetProvider>
        <AppRoutes />
      </HelmetProvider>
    </Router>
  );
}

export default App;
